import React from "react";
import {useMutation} from "react-apollo";
import {useLocation} from "react-router-dom";

import useAnalytics from "shared/AnalyticsNew/hooks/useAnalytics";
import {productSpec, sharedEvents} from "shared/AnalyticsNew/constants/productSpecification";
import {replaceMe} from "shared/Auth/actions/authActions";

import {ProspectStage as ProspectStageUI} from "./ProspectStage";
import {
    prospectStageMutation,
    isProspectAppliedStage,
    ProspectStageLocation,
    DecisionStageLayout,
    ProspectStage,
} from "./prospectStages";
import {useUniversity} from "../../../UniversityProvider/UniversityProvider";
import {
    useRecommendations,
    RECOMMENDATION_BANNER_BATCH_SIZE,
} from "../../../Recommendations/RecommendationsProvider/RecommendationsProvider";
import updateApplicantUniversity from "../../../Applicant/mutations/updateApplicantUniversityMutation";
import {getReduxDispatch} from "../../../../../App";
import useAuth from "../../../../../shared/Auth/hooks/useAuth";
import {ApplicantUniversityLinkField} from "../../../../../types/generated";

type PopupQuestionsProps = {
    onDismiss: () => void;
    isOpen: boolean;
};

export const PopupQuestions = ({onDismiss, isOpen}: PopupQuestionsProps) => {
    const dispatch = getReduxDispatch();
    const {authState} = useAuth();
    const [saveUserDecisionStage] = useMutation(prospectStageMutation);
    const [updateMyUniversityLink] = useMutation(updateApplicantUniversity);
    const {id, name, slug: universitySlug} = useUniversity();
    const {trackEvent} = useAnalytics();
    const {getRecommendationsCallback} = useRecommendations();
    const location = useLocation();

    const handleLegacySubmit = async (applied) => {
        trackEvent(sharedEvents.answeredHasAppliedUniversity.name, {
            universitySlug,
            response: applied ? "yes" : "no",
            product: location.pathname.includes(`/pwa/${universitySlug}`)
                ? productSpec.pwaUniversityWidget.NAME
                : productSpec.embeddedUniversityWidget.NAME,
        });
        if (authState?.me?.applicant) {
            dispatch(
                replaceMe({
                    ...authState.me,
                    applicant: {
                        ...authState.me.applicant,
                        applicantUniversities: (
                            authState.me.applicant.applicantUniversities ?? []
                        ).map((uniLink: ApplicantUniversityLinkField) => {
                            if (uniLink.university.slug !== universitySlug) return uniLink;

                            return {
                                ...uniLink,
                                university: {
                                    ...uniLink.university,
                                },
                                applied,
                            };
                        }),
                    },
                }),
            );
        }

        try {
            const result = await updateMyUniversityLink({
                variables: {
                    universitySlug,
                    applied,
                },
            });

            if (result?.data?.updateMyUniversityLink?.me) {
                dispatch(replaceMe(result.data.updateMyUniversityLink.me));
            }
        } catch (error) {
            console.error("Error updating university application status:", error);
        }
    };

    const handleSubmit = React.useCallback(async (prospectStage: ProspectStage) => {
        try {
            await handleLegacySubmit(isProspectAppliedStage(prospectStage));
            await saveUserDecisionStage({
                variables: {
                    universityId: id,
                    decisionStage: prospectStage,
                    prospectStageLocation: ProspectStageLocation.WIDGET_CHAT,
                },
            });
            await getRecommendationsCallback(RECOMMENDATION_BANNER_BATCH_SIZE);
        } catch (e) {
            console.error("Error submitting university applied question", e);
        }

        trackEvent(sharedEvents.prospectDecisionStageSubmitted.name, {
            universityId: id,
            universityName: name,
            prospectStage,
            where: ProspectStageLocation.WIDGET_CHAT,
            layout: DecisionStageLayout.STACK,
        });
        onDismiss();
    });

    const handleDismiss = React.useCallback(() => {
        trackEvent(sharedEvents.prospectDecisionStageDismissed.name, {
            universityId: id,
            universityName: name,
            where: ProspectStageLocation.WIDGET_CHAT,
            layout: DecisionStageLayout.STACK,
        });
        onDismiss();
    });

    return (
        <ProspectStageUI
            onDismiss={handleDismiss}
            onSubmit={handleSubmit}
            isOpen={isOpen}
            location={ProspectStageLocation.WIDGET_CHAT}
        />
    );
};
